import Image from 'next/image';
import type { MentorshipDetailView } from '@/features/mentorship';
import MentorRegisterButton from '@/features/mentorship/components/registration/MentorRegisterButton';
import { cn } from '@/utils';

type Props = {
  view: MentorshipDetailView;
};

export default function MentorshipProgramDetail({ view }: Props) {
  const {
    hero,
    guidelineHeading,
    guidelineSubheading,
    guidelines,
    mentors,
    programId,
    jobId,
    canApply,
    applyClosedReason,
    questions,
  } = view;

  const mentorImage = (m: (typeof mentors)[number]) =>
    m.imageSrc ||
    `${process.env.NEXT_PUBLIC_CDN_URL}/mentorship/mentor_${m.id}.png`;

  const mentorCount = mentors.length;
  const isFewMentors = mentorCount <= 2;

  return (
    <>
      <section className="relative w-full overflow-hidden bg-gray-100">
        <div className="relative w-full">
          <Image
            src={hero.backgroundImage}
            alt={hero.titleAlt || view.listTitle}
            width={1600}
            height={900}
            sizes="100vw"
            className="w-full h-auto object-contain object-bottom bg-gray-100"
            priority
          />
          {hero.showDownArrow ? (
            <div className="pointer-events-none absolute bottom-4 left-0 right-0 z-10 flex flex-col items-center justify-center gap-4 text-center">
              <Image
                src="/mentor/arrow-down.gif"
                alt="Scroll down"
                width={40}
                height={40}
                className="object-contain"
                unoptimized
              />
            </div>
          ) : null}
        </div>
      </section>

      <section className="relative w-full overflow-hidden">
        <div
          className="pointer-events-none select-none absolute left-0 top-20 w-[min(260px,42vw)] sm:w-[min(380px,34vw)]"
          aria-hidden="true"
        >
          <Image
            src="/mentor/m-left.png"
            alt=""
            width={304}
            height={659}
            sizes="(max-width: 640px) 42vw, 380px"
            className="h-auto w-full object-contain"
          />
        </div>
        <div
          className="pointer-events-none select-none absolute right-0 top-[min(18rem,28vh)] w-[min(280px,48vw)] sm:w-[min(420px,36vw)]"
          aria-hidden="true"
        >
          <Image
            src="/mentor/m-right.png"
            alt=""
            width={431}
            height={637}
            sizes="(max-width: 640px) 48vw, 420px"
            className="h-auto w-full object-contain"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-4">
          <div className="text-center mb-8 mt-19">
            <h2
              className="text-2xl font-semibold leading-8 tracking-[-1.2px]"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #0AC1CE 0%, #0AC1CE 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              &quot;{guidelineHeading}&quot;
            </h2>
            <p className="text-black text-2xl font-semibold leading-8 tracking-[-1.2px] text-center">
              {guidelineSubheading}
            </p>
          </div>

          <div className="space-y-4">
            {guidelines.map((block, i) => {
              if (block.type === 'paragraph') {
                return (
                  <div key={i}>
                    <p className="text-[18px] font-semibold leading-5 tracking-[-0.9px] text-black">
                      {block.title}
                    </p>
                    <div
                      className="mt-1 text-[16px] font-normal leading-6 tracking-[-0.8px] text-[#414651] [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_a]:text-[#0AC1CE] [&_a]:underline"
                      dangerouslySetInnerHTML={{ __html: block.body }}
                    />
                  </div>
                );
              }
              if (block.type === 'link') {
                return (
                  <div key={i}>
                    <p className="text-[18px] font-semibold leading-5 tracking-[-0.9px] text-black">
                      {block.title}
                    </p>
                    <a
                      href={block.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-[16px] font-normal leading-6 tracking-[-0.8px] text-[#0AC1CE] underline"
                    >
                      {block.label}
                    </a>
                  </div>
                );
              }
              if (block.type === 'qr') {
                return (
                  <div key={i}>
                    <p className="text-[18px] font-semibold leading-5 tracking-[-0.9px] text-black">
                      {block.title}
                    </p>
                    <div className="mt-2">
                      <Image
                        src={block.src}
                        alt={block.alt}
                        width={160}
                        height={160}
                        className="object-contain"
                      />
                    </div>
                  </div>
                );
              }
              return (
                <div key={i}>
                  <p className="text-[18px] font-semibold leading-5 tracking-[-0.9px] text-black">
                    {block.title}
                  </p>
                  <ul className="mt-1 space-y-1 list-none text-[16px] font-normal leading-6 tracking-[-0.8px] text-[#414651]">
                    {block.items.map((item, j) => (
                      <li key={j}>- {item}</li>
                    ))}
                  </ul>
                  {block.afterNote ? (
                    <p className="mt-2 text-[16px] font-normal leading-6 tracking-[-0.8px] text-[#414651]">
                      {block.afterNote}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {mentors.length > 0 ? (
        <section className="container mx-auto px-4 pt-10 pb-24 sm:pb-28 max-w-400">
          <div className="text-center mt-19 mb-8">
            <h2
              className="text-2xl font-semibold leading-8 tracking-[-1.2px]"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #0AC1CE 0%, #0AC1CE 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              &quot;МЕНТОРУУД&quot;
            </h2>
          </div>

          <div
            className={cn(
              isFewMentors
                ? 'flex flex-wrap justify-center gap-x-10 gap-y-11 sm:gap-x-14 sm:gap-y-12 md:gap-x-20'
                : 'grid grid-cols-2 justify-items-center sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-4 gap-y-10 sm:gap-x-6 sm:gap-y-11 md:gap-x-8 md:gap-y-13',
            )}
          >
            {mentors.map((mentor) => (
              <div
                key={mentor.id}
                className={cn(
                  'flex flex-col items-center text-center gap-3 shrink-0',
                  isFewMentors &&
                    'w-[min(100%,220px)] sm:w-[min(100%,260px)]',
                )}
              >
                <div
                  className={cn(
                    'rounded-full shrink-0 overflow-hidden ring-2 ring-black/5 shadow-sm',
                    isFewMentors
                      ? 'w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 xl:w-36 xl:h-36'
                      : 'w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 lg:w-36 lg:h-36',
                  )}
                >
                  <Image
                    src={mentorImage(mentor)}
                    alt={mentor.name}
                    width={160}
                    height={160}
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <div>
                  <p className="text-[11px] sm:text-[14px] md:text-[16px] font-semibold leading-none tracking-[-0.64px] text-black">
                    {mentor.name}
                  </p>
                  <p className="text-[10px] sm:text-[12px] md:text-[14px] font-normal leading-5 tracking-[-0.56px] text-[#535862] mt-2 opacity-60">
                    {mentor.company}
                  </p>
                  <p className="text-[10px] sm:text-[12px] md:text-[14px] font-normal leading-5 tracking-[-0.56px] text-[#535862] opacity-60">
                    {mentor.role}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-16">
            <MentorRegisterButton
              programId={programId}
              jobId={jobId}
              canApply={canApply}
              applyClosedReason={applyClosedReason}
              questions={questions}
            />
          </div>
        </section>
      ) : (
        <section className="container mx-auto px-4 pt-10 pb-24 sm:pb-28 max-w-400">
          <div className="flex justify-center mt-8">
            <MentorRegisterButton
              programId={programId}
              jobId={jobId}
              canApply={canApply}
              applyClosedReason={applyClosedReason}
              questions={questions}
            />
          </div>
        </section>
      )}
    </>
  );
}
